/*
  Accessibility enhancements — Alpyn Beauty, Concept 5.2.0
  Added 2026-10-21 from the 9-30-26 SortSite WCAG 2.1/2.2 AA report.
  Loaded after theme.js (defer) from layout/theme.liquid. Pairs with
  assets/a11y-enhancements.css and snippets/a11y-video-toggle.liquid.

  1. Pause/play toggle for autoplaying <video-media> (WCAG 2.2.2). One delegated click
     handler; the button is either inside the <video-media> or, when the video sits inside
     a link (product cards, multicolumn), outside it under a [data-a11y-video-scope] wrapper.
     Uses theme.js's own DeferredMedia API: `paused` is the flag its inView callback checks
     before it (re)starts playback, so a paused video stays paused when scrolled back in.
  2. prefers-reduced-motion: autoplaying videos start paused (WCAG 2.3.3). The toggle and the
     theme's own play buttons still let a visitor play them.
  3. "Express Checkout": sets the real text of Shopify's injected "More payment options"
     button; the old CSS-generated label (font-size: 0 + ::after) failed F87 and 2.5.3.
*/
(function () {
  if (window.__a11yEnhancementsBound) return;
  window.__a11yEnhancementsBound = true;

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  /* ---------------------------------------------------------------- video toggles */
  function mediaFor(button) {
    var media = button.closest('video-media');
    if (media) return media;
    var scope = button.closest('[data-a11y-video-scope]');
    if (!scope) return null;
    return scope.querySelector('video-media[autoplay]') || scope.querySelector('video-media');
  }

  function togglesFor(media) {
    var list = Array.prototype.slice.call(media.querySelectorAll('[data-a11y-video-toggle]'));
    var scope = media.closest('[data-a11y-video-scope]');
    if (scope) {
      Array.prototype.forEach.call(scope.querySelectorAll('[data-a11y-video-toggle]'), function (button) {
        if (list.indexOf(button) === -1 && mediaFor(button) === media) list.push(button);
      });
    }
    return list;
  }

  function sync(media) {
    var playing = media.hasAttribute('playing');
    togglesFor(media).forEach(function (button) {
      button.setAttribute('data-state', playing ? 'playing' : 'paused');
      button.setAttribute('aria-label', playing ? button.dataset.labelPause : button.dataset.labelPlay);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-a11y-video-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    var media = mediaFor(button);
    if (!media) return;
    if (media.hasAttribute('playing')) {
      media.paused = true;
      media.pause();
    } else {
      media.paused = false;
      media.play();
    }
    // Play/pause events arrive asynchronously; reflect the intent straight away too.
    button.setAttribute('data-state', media.paused ? 'paused' : 'playing');
    button.setAttribute('aria-label', media.paused ? button.dataset.labelPlay : button.dataset.labelPause);
  });

  function onMediaEvent(event) {
    var media = event.target;
    if (media && media.tagName && media.tagName.toLowerCase() === 'video-media') sync(media);
  }
  document.addEventListener('video:play', onMediaEvent);
  document.addEventListener('video:pause', onMediaEvent);

  function initVideos(root) {
    root = root || document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-a11y-video-toggle]'), function (button) {
      var media = mediaFor(button);
      if (media) sync(media);
    });
    if (reduceMotion && reduceMotion.matches) {
      Array.prototype.forEach.call(root.querySelectorAll('video-media[autoplay]'), function (media) {
        media.paused = true;
        if (media.hasAttribute('playing')) media.pause();
        sync(media);
      });
    }
  }

  /* ---------------------------------------------------------------- Express Checkout label */
  var MORE_OPTIONS_LABEL = 'Express Checkout';
  function relabelMoreOptions(root) {
    root = root || document;
    Array.prototype.forEach.call(root.querySelectorAll('.shopify-payment-button__more-options'), function (el) {
      if (el.classList.contains('shopify-payment-button__skeleton')) return;
      if (el.getAttribute('aria-hidden') === 'true') return;
      if (el.children.length === 0 && el.textContent.trim() !== MORE_OPTIONS_LABEL) {
        el.textContent = MORE_OPTIONS_LABEL;
      }
    });
  }

  var observer = window.MutationObserver ? new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if ((node.matches && node.matches('.shopify-payment-button__more-options, shopify-accelerated-checkout, .shopify-payment-button')) ||
            (node.querySelector && node.querySelector('.shopify-payment-button__more-options'))) {
          relabelMoreOptions(document);
          return;
        }
      }
      if (mutations[i].type === 'characterData' || mutations[i].type === 'attributes') {
        var target = mutations[i].target.nodeType === 1 ? mutations[i].target : mutations[i].target.parentElement;
        if (target && target.closest && target.closest('.shopify-payment-button__more-options')) {
          relabelMoreOptions(document);
          return;
        }
      }
    }
  }) : null;

  function init() {
    initVideos(document);
    relabelMoreOptions(document);
    if (observer) observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    // Shopify injects the accelerated-checkout buttons late; sweep a few times as a fallback.
    [800, 2000, 4000].forEach(function (delay) { setTimeout(function () { relabelMoreOptions(document); }, delay); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (event) {
    initVideos(event.target);
    relabelMoreOptions(event.target);
  });
})();
